<template>
  <div class="form-radio">
    <label
      class="form-radio__label display-block space-mb-1"
      :class="[
        { 'display-hidden-accessible': labelHidden },
        labelClasses
      ]"
      :for="id">
      {{ label }}
    </label>
    <component
      :is="inline ? 'span' : 'div'"
      v-for="(option, index) in options"
      :key="`${id}-${index}`"
      class="form-radio__item space-mb-1"
      :class="[
        {
          'is-active': model === option.value,
          'display-inline-block': inline,
          'space-mr-2': !full,
        },
        option.classes
      ]">
      <label class="flex align-center">
        <input
          v-model="model"
          type="radio"
          :name="name"
          :value="option.value"
          class="form-radio__input">
        <span v-if="!iconHidden" class="form-radio__icon space-mr-1" />
        <slot name="item" :option="option" :is-active="model === option.value">
          {{ option.label }}
        </slot>
      </label>
    </component>
  </div>
</template>

<script>
  import { nanoid } from 'nanoid';

  export default {
    props: {
      /**
       * Group label
       * @type {String}
       */
      label: {
        type: String,
        required: true,
      },
      labelClasses: {
        type: String,
        default: null,
      },
      /**
       * Hide the group label
       * @type {Boolean}
       */
      labelHidden: {
        type: Boolean,
        default: false,
      },
      /**
       * The name of the radio buttons group
       * @type {String}
       */
      name: {
        type: String,
        required: true,
      },
      /**
       * List of options to choose from
       * @type {Array}
       */
      options: {
        type: Array,
        required: true,
      },
      /**
       * The selected value
       * @type {mixed}
       */
      // eslint-disable-next-line vue/require-prop-types
      value: {
        required: true,
      },
      /**
       * Display the choices inline
       * @type {Boolean}
       */
      inline: {
        type: Boolean,
        default: false,
      },
      /**
       * Display the choices without margin
       * @type {Boolean}
       */
      full: {
        type: Boolean,
        default: false,
      },
      /**
       * Hide the radio icon
       * @type {Boolean}
       */
      iconHidden: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        id: nanoid(),
      };
    },
    computed: {
      model: {
        get() {
          return this.value;
        },
        set(value) {
          this.$emit('input', value);
        },
      },
    },
  };
</script>

<style lang="scss">
$radio-icon-size: math.div(20px, $font-size-root) * 1em;

.form-radio {
  display: block;
}

.form-radio__item {
  cursor: pointer;
}

.form-radio__input {
  @include display-hidden-accessible;
}

.form-radio__icon {
  position: relative;
  box-sizing: border-box;
  display: inline-block;
  background-color: c('white');
  border: 2px solid c('grey');

  &,
  &::before {
    width: $radio-icon-size;
    height: $radio-icon-size;
    border-radius: 50%;
  }

  &::before {
    content: "";
    position: absolute;
    background-color: c('white');
    opacity: 0;
    transform: scale(0);
    transition: transform 0.6s $out-expo, opacity 0.6s $out-expo;
  }

  .is-active & {
    background-color: c('green');
    border: none;

    &::before {
      opacity: 1;
      transform: scale(0.4);
    }
  }
}
</style>
